import React, { useEffect, useRef, useState } from 'react';
import '../../../desktopstyle.css';
import { computeChangedRows, getRowKey, getStageRowValue } from '../utils/highlightDiff';

const guestTextColors = [
  '#63AA57', '#6EB9E3', '#758AF8', '#8DEACE',
  '#B6F875', '#A2DDEA', '#9988FF', '#BECEF6', '#BCEAA0',
  '#ECA3A3', '#F5FF88', '#F875D3', '#FFBEA2', '#FFE5A2',
];

const createColorMapping = (data) => {
  const mapping = {};
  let colorIndex = 0;
  data.forEach((item) => {
    const g = (item.inGroup || '').trim();
    if (!g || mapping[g]) return;
    mapping[g] = guestTextColors[colorIndex % guestTextColors.length];
    colorIndex++;
  });
  return mapping;
};

const StageResultTable = ({ results, loading, error }) => {
  const [colorMapping, setColorMapping] = useState({});
  const [highlightedRows, setHighlightedRows] = useState(new Set());
  const prevResultsRef = useRef([]);
  const highlightTimerRef = useRef(null);

  useEffect(() => {
    setColorMapping(createColorMapping(results));
  }, [results]);

  useEffect(() => {
    const prev = prevResultsRef.current;
    if (prev.length === 0) { prevResultsRef.current = results; return; }

    const newHighlights = computeChangedRows(prev, results, getRowKey, getStageRowValue);

    if (newHighlights.size > 0) {
      clearTimeout(highlightTimerRef.current);
      setHighlightedRows(newHighlights);
      highlightTimerRef.current = setTimeout(() => setHighlightedRows(new Set()), 1800);
    }

    prevResultsRef.current = results;
  }, [results]);

  useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

  return (
    <div className="column is-half">
      <div className="has-text-centered"><p className="title is-4"> </p></div>
      {loading ? (
        <div className="loading-spinner"></div>
      ) : (
        <>
          {error ? (
            <div className="notification has-text-centered mt-1"><strong>{error}</strong></div>
          ) : !results.length ? (
            <div className="notification has-text-centered mt-1"><strong>Brak danych</strong></div>
          ) : (
            <div className="desk-table-container" translate="no">
              <div className="title">Odcinkowa</div>
              <div className="tab-container">
                <table>
                  <thead>
                    <tr className="table-header">
                      <th className="table-cell">pozycja</th>
                      <th className="table-cell">numer</th>
                      <th className="table-cell">załoga</th>
                      <th className="table-cell">samochód i klasa</th>
                      <th className="table-cell">czas</th>
                      <th className="table-cell">różnica</th>
                      <th className="table-cell">prędkość</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, index) => (
                      <tr
                        className={`table-row${highlightedRows.has(result.positionNumber) && result.rowNumber !== 'OUT' ? ' row-highlight' : ''}`}
                        key={result.positionNumber ?? index}
                        style={result.rowNumber === 'OUT' ? { background: '#f2f2f2', color: '#aaa' } : undefined}
                      >
                        <td className="table-cell">
                          <div className="table-cell-position">{result.rowNumber}</div>
                        </td>
                        <td className="table-cell">
                          <div className="position-badge-text">
                            <div className="position-badge">{result.positionNumber}</div>
                          </div>
                        </td>
                        <td className="table-cell table-cell-name">
                          <div style={{ whiteSpace: 'nowrap' }}>{result.driverName}</div>
                          <div style={{ whiteSpace: 'nowrap' }}>{result.coDriverName}</div>
                        </td>
                        <td className="table-cell table-cell-car">
                          <div>{result.car}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                            {(result.inGroup || '').trim() && (
                              result.classPosition != null && result.classPosition !== 'OUT' ? (
                                <div
                                  className="split-class-badge"
                                  style={{ borderColor: colorMapping[(result.inGroup || '').trim()] || '#ccc' }}
                                >
                                  <span className="split-class-badge-label" style={{ background: colorMapping[(result.inGroup || '').trim()] || '#ccc' }}>
                                    {(result.inGroup || '').trim()}
                                  </span>
                                  <span className="split-class-badge-pos">#{result.classPosition}</span>
                                </div>
                              ) : (
                                <div className="guest-badge" style={{ background: colorMapping[(result.inGroup || '').trim()] || 'transparent' }}>
                                  <div className="guest-badge-text">{result.inGroup}</div>
                                </div>
                              )
                            )}
                          </div>
                        </td>
                        <td className="table-cell table-cell-time">
                          <div className="time-cell-stack">
                            <div>{result.resultTime}</div>
                            {result.penalty && (
                              <div className="penalty">
                                <div className="penalty-text">+{result.penalty}</div>
                              </div>
                            )}
                            {result.taryfa && (
                              <div className="taryfa">
                                <div className="taryfa-text">Taryfa</div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="table-cell table-cell-difference">
                          <div>{result.diffToFirst}</div>
                          <div>{result.diffToPrev}</div>
                        </td>
                        <td className="table-cell table-cell-speed">
                          <div>{result.avgSpeedKmh != null ? `${result.avgSpeedKmh} km/h` : '—'}</div>
                          {result.speedDeficitKmh != null && result.speedDeficitKmh > 0 && (
                            <div className="speed-deficit">-{result.speedDeficitKmh} km/h</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StageResultTable;
